import axios from "axios";
import { adminApi } from "../../lib/axios";

// Payment method field interface
export interface PaymentMethodField {
  id: string;
  fieldName: string;
  label: string;
  isRequired: boolean;
}

// Payment method interface
export interface PaymentMethod {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  fields: PaymentMethodField[];
}

// Matching payment info interface
export interface MatchingPaymentInfo {
  paymentMethod: PaymentMethod;
  fieldData: Record<string, string>;
}

// Payment details interface
export interface PaymentDetails {
  fieldData: Record<string, string>;
  paymentMethodId: string;
  paymentMethodName: string;
}

// Affiliate info interface
export interface PayoutAffiliate {
  firstName: string;
  lastName: string;
  email: string;
  profileImage: string | null;
}

// Payout request interface
export interface PayoutRequest {
  id: string;
  amount: number;
  status: "PENDING" | "PAID";
  createdAt: string;
  updatedAt: string;
  paidAt: string | null;
  affiliateId: string;
  brandId: string;
  code: string;
  matchingPaymentInfos: MatchingPaymentInfo[];
  paymentDetails: PaymentDetails | null;
  affiliate: PayoutAffiliate;
  brandName?: string;
}

// Pagination interface
export interface PayoutPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Search payouts response
export interface SearchPayoutsResponse {
  data: PayoutRequest[];
  pagination: PayoutPagination;
}

// Search parameters interface
export interface PayoutSearchParams {
  page?: number;
  limit?: number;
  status?: "PENDING" | "PAID";
  affiliateId?: string;
  brandId?: string;
  affiliateIds?: string[];
  brandIds?: string[];
  search?: string;
  minAmount?: number;
  maxAmount?: number;
  createdAfter?: string;
  createdBefore?: string;
  sortBy?: "AMOUNT" | "CREATED_AT" | "UPDATED_AT" | "STATUS";
  sortOrder?: "ASC" | "DESC";
}

// Service class
export class AdminPayoutsService {
  // Get payout requests with filtering and pagination
  async getPayoutRequests(
    params: PayoutSearchParams = {}
  ): Promise<SearchPayoutsResponse> {
    try {
      const queryParams = new URLSearchParams();

      // Add pagination parameters
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());

      // Add filter parameters
      if (params.status) queryParams.append("status", params.status);
      if (params.affiliateId)
        queryParams.append("affiliateId", params.affiliateId);
      if (params.brandId) queryParams.append("brandId", params.brandId);
      if (params.affiliateIds && params.affiliateIds.length > 0) {
        params.affiliateIds.forEach((id) =>
          queryParams.append("affiliateIds", id)
        );
      }
      if (params.brandIds && params.brandIds.length > 0) {
        params.brandIds.forEach((id) => queryParams.append("brandIds", id));
      }
      if (params.search) queryParams.append("search", params.search);
      if (params.minAmount !== undefined)
        queryParams.append("minAmount", params.minAmount.toString());
      if (params.maxAmount !== undefined)
        queryParams.append("maxAmount", params.maxAmount.toString());
      if (params.createdAfter)
        queryParams.append("createdAfter", params.createdAfter);
      if (params.createdBefore)
        queryParams.append("createdBefore", params.createdBefore);

      // Add sorting parameters
      if (params.sortBy) queryParams.append("sortBy", params.sortBy);
      if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

      const response = await adminApi.get(
        `/payout/admin/requests?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to fetch payout requests"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Update payout status
  async updatePayoutStatus(
    payoutId: string,
    status: "PAID" | "CANCELLED",
    paymentDetails?: PaymentDetails
  ): Promise<void> {
    try {
      await adminApi.patch(`/payout/admin/requests/${payoutId}/status`, {
        status,
        paymentDetails,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update payout status"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  // Get payout statistics using new admin overview endpoint
  async getPayoutStats(): Promise<{
    totalPendingRequests: number;
    totalPaidRequests: number;
    totalRequests: number;
    totalPendingAmount: number;
    totalPaidAmount: number;
    totalAmount: number;
    averagePayoutPerRequest: number;
  }> {
    try {
      const response = await adminApi.get("/payout/admin/overview");
      return response.data;
    } catch (error) {
      console.error("Failed to fetch payout stats:", error);
      return {
        totalPendingRequests: 0,
        totalPaidRequests: 0,
        totalRequests: 0,
        totalPendingAmount: 0,
        totalPaidAmount: 0,
        totalAmount: 0,
        averagePayoutPerRequest: 0,
      };
    }
  }

  // Helper method to format payout data for display
  formatPayoutForDisplay(payout: PayoutRequest) {
    const affiliateName = `${payout.affiliate.firstName} ${payout.affiliate.lastName}`;

    return {
      id: payout.id,
      amount: payout.amount,
      status: payout.status,
      affiliateName,
      affiliateEmail: payout.affiliate.email,
      code: payout.code,
      createdAt: new Date(payout.createdAt).toLocaleDateString(),
      paidAt: payout.paidAt
        ? new Date(payout.paidAt).toLocaleDateString()
        : null,
      paymentMethodUsed:
        payout.paymentDetails?.paymentMethodName || "Not selected",
      hasPaymentMethods: payout.matchingPaymentInfos.length > 0,
    };
  }
}

// Export singleton instance
export const adminPayoutsService = new AdminPayoutsService();
